/**
 * @module manifest/groupRegistryDefs/allGroups
 * @description 全部配置项组的 defineGroup 声明（单一真相源）
 *
 * - `uploadKeys`：整组级（convert 按 panelLayout 组 id 收集）
 * - `items`：组内子项（formDelKey 子项级，见 upload-keys 的 CONFIG_GROUP_ITEM_UPLOAD_KEYS）
 * - `recalcOnConfirm`：Confirm 是否触发后端重算（`false` → `NOT_CALCULATE_KEYS`）
 * - `chartVisibility`：按图表类型的显隐（panelLayout 只列组 id）
 */

import { defineGroup } from '../defineGroup';
import {
  CHART_SWITCH_CHART_VISIBILITY,
  DIMENSION_PERCENTAGE_CHART_VISIBILITY,
  DISPLAY_LIMIT_CHART_VISIBILITY,
  LEGEND_SETTING_CHART_VISIBILITY,
  MERGE_SAME_CELLS_CHART_VISIBILITY,
  PERCENTAGE_OF_COLUMN_TOTAL_CHART_VISIBILITY,
  PIVOT_TABLE_CHART_VISIBILITY,
  TABLE_FIELD_SETTING_CHART_VISIBILITY,
  TOOL_TIP_SETTING_CHART_VISIBILITY,
  X_AXIS_SETTING_CHART_VISIBILITY,
  Y_AXIS_SETTING_CHART_VISIBILITY,
} from './chartVisibility';
import DataLabel from '../../settingGroups/DataLabel';
import LegendColorSetting from '../../settingGroups/LegendColorSetting';
import CountLimit from '../../settingGroups/CountLimit';
import StackedLimit from '../../settingGroups/StackedLimit';
import DisplayLimit from '../../settingGroups/DisplayLimit';
import LegendSetting from '../../settingGroups/LegendSetting';
import ChartSwitch from '../../settingGroups/ChartSwitch';
import EditReferenceLine from '../../settingGroups/EditReferenceLine';
import ToolTipSetting from '../../settingGroups/ToolTipSetting';
import TableFieldSetting from '../../settingGroups/TableFieldSetting';
import FreezePanes from '../../settingGroups/FreezePanes';
import MergeSameCells from '../../settingGroups/MergeSameCells';
import ConditionalFormat from '../../settingGroups/ConditionalFormat';
import EditStatistics from '../../settingGroups/EditStatistics';
import PercentageOfColumnTotal from '../../settingGroups/PercentageOfColumnTotal';
import DimensionPercentage from '../../settingGroups/DimensionPercentage';
import PivotTable from '../../settingGroups/PivotTable';
import MapColorSelector from '../../settingGroups/MapColorSelector';
import ColorSetting from '../../settingGroups/ColorSetting';
import XAxisSetting from '../../settingGroups/XAxisSetting';
import YAxisSetting from '../../settingGroups/YAxisSetting';
import MixedAxisConfig from '../../settingGroups/MixedAxisConfig';
import DescriptionSetting from '../../settingGroups/DescriptionSetting';
import NullHanding from '../../settingGroups/NullHanding';

export const DATA_LABEL_GROUP_DEFINITION = defineGroup({
  id: 'DataLabel',
  component: DataLabel,
  uploadKeys: ['showDataLabel', 'displayForm', 'labelContent'],
  recalcOnConfirm: false,
});

export const LEGEND_COLOR_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'LegendColorSetting',
  component: LegendColorSetting,
  uploadKeys: ['legend_colors'],
  recalcOnConfirm: false,
});

export const COUNT_LIMIT_GROUP_DEFINITION = defineGroup({
  id: 'CountLimit',
  component: CountLimit,
  uploadKeys: ['count'],
});

const STACKED_LIMIT_UPLOAD_KEYS = [
  'stackedCount',
  'stackedDynamicState',
  'stackedShowOthers',
] as const;

export const STACKED_LIMIT_GROUP_DEFINITION = defineGroup({
  id: 'StackedLimit',
  component: StackedLimit,
  uploadKeys: [...STACKED_LIMIT_UPLOAD_KEYS],
});

export const DISPLAY_LIMIT_GROUP_DEFINITION = defineGroup({
  id: 'DisplayLimit',
  component: DisplayLimit,
  uploadKeys: [...STACKED_LIMIT_UPLOAD_KEYS],
  chartVisibility: DISPLAY_LIMIT_CHART_VISIBILITY,
});

export const LEGEND_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'LegendSetting',
  component: LegendSetting,
  uploadKeys: ['legend_position', 'sort_series', 'series_order', 'series_sorts'],
  chartVisibility: LEGEND_SETTING_CHART_VISIBILITY,
  items: [
    {
      id: 'LegendPosition',
      uploadKeys: ['legend_position'],
      recalcOnConfirm: false,
    },
    {
      id: 'SeriesSortBy',
      uploadKeys: ['sort_series', 'series_order', 'series_sorts'],
    },
  ],
});

export const CHART_SWITCH_GROUP_DEFINITION = defineGroup({
  id: 'ChartSwitch',
  component: ChartSwitch,
  uploadKeys: ['chart_switch'],
  recalcOnConfirm: false,
  chartVisibility: CHART_SWITCH_CHART_VISIBILITY,
});

export const EDIT_REFERENCE_LINE_GROUP_DEFINITION = defineGroup({
  id: 'EditReferenceLine',
  component: EditReferenceLine,
  uploadKeys: ['reference_line'],
  recalcOnConfirm: false,
});

export const TOOL_TIP_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'ToolTipSetting',
  component: ToolTipSetting,
  uploadKeys: ['tooltipSetting'],
  chartVisibility: TOOL_TIP_SETTING_CHART_VISIBILITY,
});

export const TABLE_FIELD_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'TableFieldSetting',
  component: TableFieldSetting,
  uploadKeys: ['column_sort', 'field_setting'],
  recalcOnConfirm: false,
  chartVisibility: TABLE_FIELD_SETTING_CHART_VISIBILITY,
});

export const FREEZE_PANES_GROUP_DEFINITION = defineGroup({
  id: 'FreezePanes',
  component: FreezePanes,
  uploadKeys: ['freeze'],
  recalcOnConfirm: false,
});

export const MERGE_SAME_CELLS_GROUP_DEFINITION = defineGroup({
  id: 'MergeSameCells',
  component: MergeSameCells,
  uploadKeys: ['merge_same_cells'],
  chartVisibility: MERGE_SAME_CELLS_CHART_VISIBILITY,
});

export const CONDITIONAL_FORMAT_GROUP_DEFINITION = defineGroup({
  id: 'ConditionalFormat',
  component: ConditionalFormat,
  uploadKeys: ['conditional_format'],
});

export const EDIT_STATISTICS_GROUP_DEFINITION = defineGroup({
  id: 'EditStatistics',
  component: EditStatistics,
  uploadKeys: ['statistics'],
});

export const PERCENTAGE_OF_COLUMN_TOTAL_GROUP_DEFINITION = defineGroup({
  id: 'PercentageOfColumnTotal',
  component: PercentageOfColumnTotal,
  uploadKeys: ['column_percentage'],
  chartVisibility: PERCENTAGE_OF_COLUMN_TOTAL_CHART_VISIBILITY,
});

export const DIMENSION_PERCENTAGE_GROUP_DEFINITION = defineGroup({
  id: 'DimensionPercentage',
  component: DimensionPercentage,
  uploadKeys: ['dimension_percentage'],
  chartVisibility: DIMENSION_PERCENTAGE_CHART_VISIBILITY,
});

export const PIVOT_TABLE_GROUP_DEFINITION = defineGroup({
  id: 'PivotTable',
  component: PivotTable,
  uploadKeys: ['crosstab'],
  chartVisibility: PIVOT_TABLE_CHART_VISIBILITY,
});

export const MAP_COLOR_SELECTOR_GROUP_DEFINITION = defineGroup({
  id: 'MapColorSelector',
  component: MapColorSelector,
  uploadKeys: ['map_colors'],
  recalcOnConfirm: false,
});

export const COLOR_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'ColorSetting',
  component: ColorSetting,
  uploadKeys: ['colorSetting'],
  recalcOnConfirm: false,
});

export const X_AXIS_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'XAxisSetting',
  component: XAxisSetting,
  chartVisibility: X_AXIS_SETTING_CHART_VISIBILITY,
  uploadKeys: [
    'xAxisName',
    'sortBy',
    'chartSorts',
    'customMetricSort',
    'xAxisValues',
    'x_label',
    'count',
    'dynamicState',
    'showOthers',
  ],
  items: [
    { id: 'XAxisName', uploadKeys: ['xAxisName'], recalcOnConfirm: false },
    {
      id: 'SortBy',
      uploadKeys: ['sortBy', 'chartSorts', 'customMetricSort', 'xAxisValues'],
    },
    { id: 'TopN', uploadKeys: ['count', 'dynamicState', 'showOthers'] },
    { id: 'XAxisLabelDirection', uploadKeys: ['x_label'], recalcOnConfirm: false },
  ],
});

export const Y_AXIS_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'YAxisSetting',
  component: YAxisSetting,
  chartVisibility: Y_AXIS_SETTING_CHART_VISIBILITY,
  uploadKeys: [
    'yAxisName',
    'yAxisRangeSwitch',
    'dualYAxisEnabled',
    'primaryYAxis',
    'secondYAxis',
    'count',
    'dynamicState',
    'showOthers',
    'sortBy',
    'chartSorts',
    'customMetricSort',
    'xAxisValues',
  ],
  items: [
    {
      id: 'DualYAxis',
      uploadKeys: ['dualYAxisEnabled', 'primaryYAxis', 'secondYAxis'],
    },
    { id: 'YAxisName', uploadKeys: ['yAxisName'], recalcOnConfirm: false },
    {
      id: 'YAxisRange',
      uploadKeys: ['yAxisRangeSwitch'],
      recalcOnConfirm: false,
    },
    { id: 'TopN', uploadKeys: ['count', 'dynamicState', 'showOthers'] },
    {
      id: 'SortBy',
      uploadKeys: ['sortBy', 'chartSorts', 'customMetricSort', 'xAxisValues'],
    },
  ],
});

export const MIXED_AXIS_CONFIG_GROUP_DEFINITION = defineGroup({
  id: 'MixedAxisConfig',
  component: MixedAxisConfig,
  uploadKeys: ['primaryYAxis', 'secondYAxis'],
});

export const DESCRIPTION_SETTING_GROUP_DEFINITION = defineGroup({
  id: 'DescriptionSetting',
  component: DescriptionSetting,
  uploadKeys: ['description'],
  recalcOnConfirm: false,
});

export const NULL_HANDING_GROUP_DEFINITION = defineGroup({
  id: 'NullHanding',
  component: NullHanding,
  uploadKeys: ['null_ui'],
  recalcOnConfirm: false,
});

/** 全量配置项组声明（顺序与 groupRegistry 历史一致，便于 diff） */
export const ALL_REGISTERED_GROUP_DEFS = [
  DATA_LABEL_GROUP_DEFINITION,
  LEGEND_COLOR_SETTING_GROUP_DEFINITION,
  COUNT_LIMIT_GROUP_DEFINITION,
  STACKED_LIMIT_GROUP_DEFINITION,
  DISPLAY_LIMIT_GROUP_DEFINITION,
  LEGEND_SETTING_GROUP_DEFINITION,
  CHART_SWITCH_GROUP_DEFINITION,
  EDIT_REFERENCE_LINE_GROUP_DEFINITION,
  TOOL_TIP_SETTING_GROUP_DEFINITION,
  TABLE_FIELD_SETTING_GROUP_DEFINITION,
  FREEZE_PANES_GROUP_DEFINITION,
  MERGE_SAME_CELLS_GROUP_DEFINITION,
  CONDITIONAL_FORMAT_GROUP_DEFINITION,
  EDIT_STATISTICS_GROUP_DEFINITION,
  PERCENTAGE_OF_COLUMN_TOTAL_GROUP_DEFINITION,
  DIMENSION_PERCENTAGE_GROUP_DEFINITION,
  PIVOT_TABLE_GROUP_DEFINITION,
  MAP_COLOR_SELECTOR_GROUP_DEFINITION,
  COLOR_SETTING_GROUP_DEFINITION,
  X_AXIS_SETTING_GROUP_DEFINITION,
  Y_AXIS_SETTING_GROUP_DEFINITION,
  MIXED_AXIS_CONFIG_GROUP_DEFINITION,
  DESCRIPTION_SETTING_GROUP_DEFINITION,
  NULL_HANDING_GROUP_DEFINITION,
] as const;
